import { CrudPage } from '../../components/CrudPage';
import type { CsvImportConfigDto } from '../../api/models/CsvImportConfigDto';
import { CsvImportConfigControllerService } from '../../api/services/CsvImportConfigControllerService';
import CsvImportConfigFormDialog, { type CsvImportConfigFormValues } from '../forms/CsvImportConfigFormDialog';
import { DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX } from '../../utils/demandCsvImportPipeline';
import { useErrorHandler } from '../../utils/errorHandler';
import { useTranslation } from 'react-i18next';

const toFormValues = (dto: CsvImportConfigDto): CsvImportConfigFormValues => ({
  id: dto.uuid ?? '',
  entityType: dto.entityType ?? 'demands',
  description: dto.description ?? '',
  fieldMapping: dto.fieldMapping ?? {},
  valueMappers: (dto.valueMappers ?? {}) as Record<string, { function?: string; format?: string; strategy?: string; sourceColumn?: string }>,
  separator: dto.separator ?? ';',
  decimalSeparator: dto.decimalSeparator ?? ',',
  encoding: dto.encoding ?? 'UTF-8',
  skipRows: dto.skipRows ?? 0,
  defaultValues: dto.defaultValues ?? {},
  rowFilter: dto.rowFilter ?? undefined,
  runParameterFields: dto.runParameterFields ?? [],
  demandImportAllowedMaterialUuids: Array.isArray((dto.demandImportOptions as Record<string, unknown> | undefined)?.allowedMaterialUuids)
    ? ((dto.demandImportOptions as { allowedMaterialUuids: string[] }).allowedMaterialUuids)
    : [],
  demandImportOrderForecastWeeksAhead: (() => {
    const w = Number((dto.demandImportOptions as Record<string, unknown> | undefined)?.orderForecastWeeksAhead ?? 0);
    if (!Number.isFinite(w)) return 0;
    return Math.max(0, Math.min(DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX, Math.floor(w)));
  })(),
});

const toDto = (formValues: CsvImportConfigFormValues): CsvImportConfigDto => ({
  uuid: formValues.id || undefined,
  entityType: formValues.entityType?.trim() || undefined,
  description: formValues.description?.trim() || undefined,
  fieldMapping: formValues.fieldMapping ?? {},
  valueMappers: formValues.valueMappers ?? {},
  separator: formValues.separator || ';',
  decimalSeparator: formValues.decimalSeparator || ',',
  encoding: formValues.encoding || 'UTF-8',
  skipRows: formValues.skipRows ?? 0,
  defaultValues: formValues.defaultValues ?? undefined,
  rowFilter: formValues.rowFilter?.trim() || undefined,
  runParameterFields: formValues.runParameterFields ?? undefined,
  demandImportOptions: (() => {
    const allowed = formValues.demandImportAllowedMaterialUuids?.filter(Boolean) ?? [];
    const weeks = Math.max(
      0,
      Math.min(
        DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX,
        Math.floor(Number(formValues.demandImportOrderForecastWeeksAhead ?? 0)) || 0
      )
    );
    if (allowed.length === 0 && weeks === 0) {
      return undefined;
    }
    const o: Record<string, unknown> = {};
    if (allowed.length > 0) {
      o.allowedMaterialUuids = allowed;
    }
    if (weeks > 0) {
      o.orderForecastWeeksAhead = weeks;
    }
    return Object.keys(o).length > 0 ? o : undefined;
  })(),
});

const CsvImportConfigPage = () => {
  const { handleError } = useErrorHandler();
  const { t } = useTranslation(['common']);

  const fetchData = async (): Promise<CsvImportConfigFormValues[]> => {
    try {
      const configs = await CsvImportConfigControllerService.findAll();
      return (configs || []).map(toFormValues);
    } catch (error) {
      handleError(error, 'fetching csv import configs');
      return [];
    }
  };

  const saveData = async (data: CsvImportConfigFormValues): Promise<CsvImportConfigFormValues | void> => {
    try {
      const dto = toDto(data);
      const trimmedId = data.id?.trim();
      if (trimmedId) {
        const saved = await CsvImportConfigControllerService.update(trimmedId, dto);
        return toFormValues(saved);
      }

      if (dto.entityType) {
        try {
          const existing = await CsvImportConfigControllerService.getConfigByEntityType(dto.entityType);
          if (existing?.uuid) {
            const saved = await CsvImportConfigControllerService.update(existing.uuid, dto);
            return toFormValues(saved);
          }
        } catch (error: any) {
          if (error?.status !== 404) {
            throw error;
          }
        }
      }

      const saved = await CsvImportConfigControllerService.create(dto);
      return toFormValues(saved);
    } catch (error) {
      handleError(error, 'saving csv import config');
      throw error;
    }
  };

  const deleteData = async (id: string): Promise<void> => {
    try {
      await CsvImportConfigControllerService.delete(id);
    } catch (error) {
      handleError(error, 'deleting csv import config');
      throw error;
    }
  };

  return (
    <CrudPage<CsvImportConfigFormValues>
      titleKey="csvImport.title"
      gridMeta={[
        { field: 'entityType', headerKey: 'csvImport.entityType', width: 140, render: (data) => (
          data.entityType === 'demands' ? t('routes.demand') : data.entityType
        ) },
        { field: 'description', headerKey: 'csvImport.description', width: 240 },
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={CsvImportConfigFormDialog}
      onAdd={() => ({
        id: '',
        entityType: 'demands',
        description: '',
        fieldMapping: {},
        runParameterFields: [],
        valueMappers: {},
        separator: ';',
        decimalSeparator: ',',
        encoding: 'UTF-8',
        skipRows: 0,
        defaultValues: {},
        rowFilter: undefined,
        demandImportAllowedMaterialUuids: [],
        demandImportOrderForecastWeeksAhead: 0,
      })}
      entityName="csv-import-config"
      showAddButton={true}
      showEditButton={true}
      showDeleteButton={false}
      showCloneButton={true}
      allowSorting={true}
    />
  );
};

export default CsvImportConfigPage;
